import { memo, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Empty, Modal, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, currencyFmt } from './shared';
import { formatClock, formatDuration } from './OpenTicketsWidget';
import { queryDataSource } from '../../api/query';
import type { KabinPlanDashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: KabinPlanDashboardWidget;
  sourceId: number | null | undefined;
  detaySourceId: number | null | undefined;
}

const numberFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });
const VARSAYILAN_RENK = 'var(--color-primary)';

export interface Kabin {
  adisyon: string;
  taraf: string;
  bolum: string;
  kabin: string;
  acilis: string;
  acikDk: number;
  tutar: number;
  kisi: number;
  tellak: boolean;
}

interface Bolum {
  ad: string;
  kapasite: number;
  kabinler: Kabin[];
}

export interface Taraf {
  ad: string;
  kisi: number;
  tutar: number;
  kabinSayisi: number;
  bolumler: Bolum[];
}

function sirala<T>(liste: T[], ad: (x: T) => string, sira: string[] | undefined): T[] {
  const yer = (x: T) => {
    const i = sira?.indexOf(ad(x)) ?? -1;
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  return liste.slice().sort((a, b) => yer(a) - yer(b));
}

/**
 * Rapor satırlarını taraf → bölüm → kabin ağacına çevirir. Adisyonu boş satır yalnız bölümün
 * varlığını ve kapasitesini bildirir (boş bölüm de "Şu an boş" olarak görünsün diye).
 */
export function kabinAgaci(
  rows: Record<string, unknown>[],
  tarafSirasi?: string[],
  bolumSirasi?: string[],
): Taraf[] {
  const taraflar = new Map<string, Map<string, Bolum>>();
  for (const r of rows) {
    const taraf = String(r.taraf ?? '');
    const bolumAdi = String(r.bolum ?? '');
    if (!taraf || !bolumAdi) continue;
    const bolumler = taraflar.get(taraf) ?? new Map<string, Bolum>();
    taraflar.set(taraf, bolumler);
    const bolum = bolumler.get(bolumAdi) ?? { ad: bolumAdi, kapasite: 0, kabinler: [] };
    bolumler.set(bolumAdi, bolum);
    bolum.kapasite = Math.max(bolum.kapasite, Number(r.kapasite) || 0);
    if (r.adisyon === null || r.adisyon === undefined || r.adisyon === '') continue;
    bolum.kabinler.push({
      adisyon: String(r.adisyon),
      taraf,
      bolum: bolumAdi,
      kabin: String(r.kabin ?? ''),
      acilis: String(r.acilis ?? ''),
      acikDk: Number(r.acik_dk) || 0,
      tutar: Number(r.tutar) || 0,
      kisi: Math.max(1, Number(r.kisi) || 0),
      tellak: r.tellak === true || r.tellak === 't' || r.tellak === 'true',
    });
  }
  const sonuc: Taraf[] = [];
  for (const [ad, bolumMap] of taraflar) {
    const bolumler = sirala([...bolumMap.values()], (b) => b.ad, bolumSirasi);
    for (const b of bolumler) b.kabinler.sort((x, y) => y.acikDk - x.acikDk);
    const kabinler = bolumler.flatMap((b) => b.kabinler);
    sonuc.push({
      ad,
      bolumler,
      kabinSayisi: kabinler.length,
      kisi: kabinler.reduce((s, k) => s + k.kisi, 0),
      tutar: kabinler.reduce((s, k) => s + k.tutar, 0),
    });
  }
  return sirala(sonuc, (t) => t.ad, tarafSirasi);
}

interface DetaySatiri {
  kalem: string;
  adet: number;
  tutar: number;
  saat: string;
}

function KabinDetayi({ kabin, detaySourceId }: { kabin: Kabin; detaySourceId: number | null | undefined }) {
  const sorgu = useQuery({
    queryKey: ['kabin-adisyon', detaySourceId, kabin.adisyon],
    queryFn: async () => {
      const res = await queryDataSource({ sourceId: detaySourceId!, parameters: { '@ADISYON': kabin.adisyon } });
      if (!res.success) throw new Error(res.errorMessage ?? 'Sorgu başarısız');
      return ((res.data as Record<string, unknown>[]) ?? []).map<DetaySatiri>((r) => ({
        kalem: String(r.kalem ?? ''),
        adet: Number(r.adet) || 0,
        tutar: Number(r.tutar) || 0,
        saat: String(r.saat ?? ''),
      }));
    },
    enabled: !!detaySourceId,
    staleTime: 1000 * 60,
    retry: 1,
  });

  if (!detaySourceId) return <Empty description="Kalem listesi bu panelde tanımlı değil." />;
  if (sorgu.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (sorgu.isError) return <Empty description="Kalemler alınamadı." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const satirlar = sorgu.data ?? [];
  if (satirlar.length === 0) return <Empty description="Adisyonda henüz kalem yok." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const toplam = satirlar.reduce((s, x) => s + x.tutar, 0);

  return (
    <table data-testid="kabin-detay" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ color: 'var(--color-text-muted)', fontSize: 11.5 }}>
          <th style={{ ...th, textAlign: 'left' }}>Kalem</th>
          <th style={th}>Adet</th>
          <th style={th}>Tutar</th>
          <th style={th}>Eklendi</th>
        </tr>
      </thead>
      <tbody className="tnum">
        {satirlar.map((x, i) => (
          <tr key={`${x.kalem}-${i}`} style={{ borderTop: '1px solid var(--color-border)' }}>
            <td style={{ ...td, textAlign: 'left' }}>{x.kalem}</td>
            <td style={td}>{numberFmt.format(x.adet)}</td>
            <td style={td}>{currencyFmt.format(x.tutar)}</td>
            <td style={{ ...td, color: 'var(--color-text-muted)' }}>{formatClock(x.saat)}</td>
          </tr>
        ))}
        <tr style={{ borderTop: '1px solid var(--color-border-strong, var(--color-border))' }}>
          <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>Adisyon toplamı</td>
          <td style={td} />
          <td data-testid="kabin-detay-toplam" style={{ ...td, fontWeight: 700 }}>{currencyFmt.format(toplam)}</td>
          <td style={td} />
        </tr>
      </tbody>
    </table>
  );
}

function KabinPlanWidgetImpl({ widget, sourceId, detaySourceId }: Props) {
  const { config } = widget;
  const uyariDk = config.uyariDk ?? 120;
  const alarmDk = config.alarmDk ?? 180;
  const query = useWidgetData(sourceId, `kabin-plan-${widget.reportCode}`);
  const taraflar = useMemo(
    () => kabinAgaci(query.data ?? [], config.tarafSirasi, config.bolumSirasi),
    [query.data, config.tarafSirasi, config.bolumSirasi],
  );
  const [secili, setSecili] = useState<Kabin | null>(null);
  const renk = (taraf: string) => config.renkler?.[taraf] ?? VARSAYILAN_RENK;

  return (
    <div data-testid="kabin-plan-widget">
      <Panel title={config.title} subtitle={config.subtitle}>
        {query.isLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : query.isError ? (
          <Empty
            description="Salon verisi alınamadı."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: 40, color: 'var(--color-warning)' }}
          />
        ) : taraflar.length === 0 ? (
          <Empty description="Salon tanımı bulunamadı." style={{ padding: 40 }} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ padding: 4 }}>
            {taraflar.map((t) => (
              <section key={t.ad} data-testid={`taraf-${t.ad}`} aria-label={t.ad} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 8,
                    paddingBottom: 6,
                    borderBottom: `2px solid ${renk(t.ad)}`,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4, color: renk(t.ad), textTransform: 'uppercase' }}>
                    {t.ad}
                  </span>
                  <span className="tnum" style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>
                    {t.kisi} kişi · {t.kabinSayisi} kabin · {currencyFmt.format(t.tutar)}
                  </span>
                </div>
                {t.bolumler.map((b) => (
                  <div key={b.ad} data-testid={`bolum-${t.ad}-${b.ad}`} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                      <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{b.ad}</span>
                      <span className="tnum" style={{ color: 'var(--color-text-muted)' }}>
                        {b.kapasite > 0 ? `${b.kabinler.length} / ${b.kapasite} dolu` : `${b.kabinler.length} dolu`}
                      </span>
                    </div>
                    {b.kabinler.length === 0 ? (
                      <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Şu an boş</span>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 8 }}>
                        {b.kabinler.map((k) => {
                          const alarm = k.acikDk >= alarmDk;
                          const uyari = !alarm && k.acikDk >= uyariDk;
                          return (
                            <button
                              key={k.adisyon}
                              type="button"
                              data-testid={`kabin-${k.adisyon}`}
                              data-durum={alarm ? 'alarm' : uyari ? 'uyari' : 'normal'}
                              aria-haspopup="dialog"
                              aria-label={`${k.kabin}, ${formatDuration(k.acikDk)}, ${currencyFmt.format(k.tutar)}, ${k.kisi} kişi`}
                              onClick={() => setSecili(k)}
                              style={{
                                textAlign: 'left',
                                cursor: 'pointer',
                                font: 'inherit',
                                color: 'var(--color-text-primary)',
                                minHeight: 72,
                                padding: '8px 10px',
                                borderRadius: 6,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                border: `1px solid ${alarm ? 'var(--color-negative)' : uyari ? 'var(--color-warning)' : 'var(--color-border)'}`,
                                background: alarm
                                  ? 'color-mix(in srgb, var(--color-negative) 10%, var(--color-card-bg))'
                                  : uyari
                                    ? 'color-mix(in srgb, var(--color-warning) 12%, var(--color-card-bg))'
                                    : 'var(--color-card-bg)',
                              }}
                            >
                              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 6, fontSize: 12.5 }}>
                                <b>{k.kabin}</b>
                                <span className="tnum" style={{ fontWeight: 600, color: alarm ? 'var(--color-negative)' : 'var(--color-text-muted)' }}>
                                  {formatDuration(k.acikDk)}
                                </span>
                              </span>
                              <span className="serif tnum" style={{ fontSize: 16, fontWeight: 700 }}>{currencyFmt.format(k.tutar)}</span>
                              <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                {k.kisi} kişi{k.tellak ? ' · tellak' : ''}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}
      </Panel>

      <Modal
        open={secili !== null}
        onCancel={() => setSecili(null)}
        footer={null}
        destroyOnHidden
        title={
          secili && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span>
                <span style={{ color: renk(secili.taraf) }}>{secili.taraf}</span> · {secili.bolum} · {secili.kabin}
              </span>
              <span className="tnum" style={{ fontSize: 12.5, fontWeight: 400, color: 'var(--color-text-muted)' }}>
                {formatClock(secili.acilis)}'te açıldı · {formatDuration(secili.acikDk)} · {secili.kisi} kişi
              </span>
            </div>
          )
        }
      >
        {secili && <KabinDetayi kabin={secili} detaySourceId={detaySourceId} />}
      </Modal>
    </div>
  );
}

const th: React.CSSProperties = { padding: '6px 8px', fontWeight: 600, textAlign: 'right' };
const td: React.CSSProperties = { padding: '6px 8px', textAlign: 'right', color: 'var(--color-text-primary)' };

export const KabinPlanWidget = memo(KabinPlanWidgetImpl);
